import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useNotifications } from "@/hooks/useNotifications";
import { PostForm } from "./posts/PostForm";
import { PostsList } from "./posts/PostsList";

interface PostsManagerProps {
  gymId?: string;
}

export const PostsManager = ({ gymId }: PostsManagerProps) => {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingData, setEditingData] = useState<{ title: string; content: string } | null>(null);
  const { success, error: showError } = useNotifications();

  const { data: posts, refetch } = useQuery({
    queryKey: ["posts", gymId],
    queryFn: async () => {
      console.log("PostsManager: Fetching posts for gym:", gymId);
      
      if (!gymId) {
        console.warn("PostsManager: No gymId provided");
        return [];
      }

      const query = supabase
        .from("posts")
        .select("*")
        .eq("gym_id", gymId) // Siempre filtrar por gym_id específico
        .order("created_at", { ascending: false });

      const { data, error } = await query;
      
      if (error) {
        console.error("PostsManager: Error fetching posts:", error);
        throw error;
      }
      
      console.log(`PostsManager: Retrieved ${data?.length || 0} posts for gym ${gymId}`);
      return data || [];
    },
    enabled: !!gymId, // Solo ejecutar si hay gymId
  });

  const handleEdit = (post: any) => {
    setEditingId(post.id);
    setEditingData({
      title: post.title,
      content: post.content,
    });
  };

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase.from("posts").delete().eq("id", id);
      if (error) throw error;
      success("Post eliminado correctamente");
      refetch();
    } catch (error) {
      showError("No se pudo eliminar el post");
    }
  };

  const handleSuccess = () => {
    setEditingId(null);
    setEditingData(null);
    refetch();
  };

  return (
    <div className="space-y-6">
      <PostForm
        editingId={editingId}
        gymId={gymId}
        onSuccess={handleSuccess}
        initialData={editingData}
      />
      <PostsList
        posts={posts || []}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />
    </div>
  );
};